import React from 'react';

/**
 * FilterPills — Design system filter toggle pills (Phase 7)
 * Replaces all inconsistent filter buttons system-wide.
 *
 * Props:
 *   options: Array<{ value: string, label: string }>
 *   value: string | string[]
 *   onChange: (value: string | string[]) => void
 *   multiple?: boolean
 */
const FilterPills = ({
  options = [],
  value,
  onChange,
  multiple = false,
}) => {
  const isActive = (optValue) => {
    if (multiple && Array.isArray(value)) {
      return value.includes(optValue);
    }
    return String(value) === String(optValue);
  };

  const handleClick = (optValue) => {
    if (multiple) {
      const arr = Array.isArray(value) ? [...value] : [];
      const idx = arr.indexOf(optValue);
      if (idx >= 0) {
        arr.splice(idx, 1);
      } else {
        arr.push(optValue);
      }
      onChange(arr);
    } else {
      onChange(optValue);
    }
  };

  return (
    <div style={{
      display: 'flex',
      gap: '6px',
      flexWrap: 'wrap',
      alignItems: 'center',
    }}>
      {options.map((option) => {
        const active = isActive(option.value);
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => handleClick(option.value)}
            style={{
              padding: '6px 14px',
              borderRadius: '9999px',
              fontSize: '12px',
              fontWeight: active ? 600 : 400,
              fontFamily: 'var(--dp-font)',
              cursor: 'pointer',
              border: active
                ? '1px solid rgba(13, 174, 255, 0.4)'
                : '1px solid var(--dp-border-subtle)',
              background: active
                ? 'linear-gradient(135deg, rgba(13, 174, 255, 0.12), rgba(13, 174, 255, 0.06))'
                : 'transparent',
              color: active
                ? 'var(--dp-blue-500, #0DAEFF)'
                : 'var(--dp-text-tertiary)',
              boxShadow: active
                ? 'inset 0 1px 2px rgba(13, 174, 255, 0.1), 0 0 8px rgba(13, 174, 255, 0.08)'
                : 'none',
              transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
              outline: 'none',
              whiteSpace: 'nowrap',
              lineHeight: '1.4',
            }}
            onMouseEnter={(e) => {
              if (!active) {
                e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
                e.currentTarget.style.background = 'rgba(13, 174, 255, 0.04)';
                e.currentTarget.style.color = 'var(--dp-text-secondary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!active) {
                e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--dp-text-tertiary)';
              }
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default FilterPills;
